// @ts-nocheck
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  ShieldAlert,
  ShieldCheck,
  CheckCircle,
  XCircle,
  AlertTriangle,
  ChevronDown,
  ChevronUp,
  RotateCcw,
  Clock,
  Zap,
  Eye,
  FileText,
} from 'lucide-react';
import { api } from '../utils/api.js';

// ---------------------------------------------------------------------------
// Shared sub-components
// ---------------------------------------------------------------------------

const STATUS_CONFIG = {
  observing:           { label: 'Observing',        bg: 'var(--color-surface-2)', text: 'var(--color-text-3)',    border: 'var(--color-border)' },
  proposed:            { label: 'Proposed',         bg: 'var(--color-surface-2)', text: 'var(--color-text-3)',    border: 'var(--color-border)' },
  critic_cleared:      { label: 'Awaiting Review',  bg: 'var(--color-yellow-bg)', text: 'var(--color-yellow-text)', border: 'var(--color-yellow-border)' },
  approved_for_canary: { label: 'Ready for Canary', bg: 'var(--color-green-bg)',  text: 'var(--color-green-text)',  border: 'var(--color-green-border)' },
  canary_live:         { label: 'In Canary',        bg: 'var(--color-yellow-bg)', text: 'var(--color-yellow-text)', border: 'var(--color-yellow-border)' },
  live:                { label: 'Live',             bg: 'var(--color-green-bg)',  text: 'var(--color-green-text)',  border: 'var(--color-green-border)' },
  reverted:            { label: 'Reverted',         bg: 'var(--color-red-bg)',    text: 'var(--color-red-text)',    border: 'var(--color-red-border)' },
  blocked:             { label: 'Blocked',          bg: 'var(--color-red-bg)',    text: 'var(--color-red-text)',    border: 'var(--color-red-border)' },
};

function StatusBadge({ status }) {
  const c = STATUS_CONFIG[status] || { label: status, bg: 'var(--color-surface-2)', text: 'var(--color-text-3)', border: 'var(--color-border)' };
  return (
    <span style={{
      display: 'inline-block',
      padding: '3px 10px',
      borderRadius: 999,
      fontSize: 12,
      fontWeight: 600,
      background: c.bg,
      color: c.text,
      border: `1px solid ${c.border}`,
    }}>
      {c.label}
    </span>
  );
}

function BlastBadge({ blast_radius }) {
  const map = {
    narrow: { label: 'Narrow', color: 'var(--color-green-text)' },
    wide: { label: 'Wide', color: 'var(--color-yellow-text)' },
    pipeline: { label: 'Pipeline', color: 'var(--color-red-text)' },
  };
  const b = map[blast_radius] || { label: blast_radius, color: 'var(--color-text-3)' };
  return <span style={{ fontSize: 12, fontWeight: 600, color: b.color }}>{b.label}</span>;
}

// ---------------------------------------------------------------------------
// Circuit Breaker Banner
// ---------------------------------------------------------------------------

function CircuitBreakerBanner({ circuitBreaker, waitingCount, onReset }) {
  if (!circuitBreaker || circuitBreaker.auto_apply_enabled) return null;

  return (
    <div style={{
      background: 'var(--color-red-bg)',
      border: '1px solid var(--color-red-border)',
      borderRadius: 12,
      padding: '16px 24px',
      marginBottom: 24,
      display: 'flex',
      alignItems: 'center',
      gap: 16,
      flexWrap: 'wrap',
    }}>
      <ShieldAlert size={20} style={{ color: 'var(--color-red-text)', flexShrink: 0 }} />
      <div style={{ flex: 1, minWidth: 200 }}>
        <div style={{ fontWeight: 700, fontSize: 14, color: 'var(--color-red-text)', marginBottom: 4 }}>
          Auto-apply disabled
        </div>
        <div style={{ fontSize: 13, color: 'var(--color-red-text)', opacity: 0.85 }}>
          {circuitBreaker.disabled_reason || 'Circuit breaker tripped.'}
          {waitingCount > 0 && ` ${waitingCount} proposal${waitingCount !== 1 ? 's' : ''} waiting for review.`}
        </div>
        {circuitBreaker.disabled_at && (
          <div style={{ fontSize: 12, color: 'var(--color-red-text)', opacity: 0.65, marginTop: 4 }}>
            Disabled {new Date(circuitBreaker.disabled_at).toLocaleString()}
          </div>
        )}
      </div>
      <button
        onClick={onReset}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          padding: '8px 16px',
          borderRadius: 8,
          border: '1px solid var(--color-red-border)',
          background: 'transparent',
          color: 'var(--color-red-text)',
          fontWeight: 600,
          fontSize: 13,
          cursor: 'pointer',
        }}
      >
        <RotateCcw size={14} />
        Re-enable auto-apply
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Approval Card
// ---------------------------------------------------------------------------

function ProposalCard({ proposal, onApprove, onReject, actionLoading }) {
  const [expanded, setExpanded] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [showRejectInput, setShowRejectInput] = useState(false);

  const hasObjection = proposal.critic_verdict === 'BLOCK' && proposal.critic_objection;

  return (
    <div style={{
      background: 'var(--color-surface)',
      border: '1px solid var(--color-border)',
      borderRadius: 14,
      overflow: 'hidden',
      marginBottom: 16,
      borderLeft: hasObjection ? '4px solid var(--color-yellow-text)' : undefined,
    }}>
      {/* Header */}
      <div style={{ padding: '20px 24px', display: 'flex', alignItems: 'flex-start', gap: 16 }}>
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8, flexWrap: 'wrap' }}>
            <span style={{ fontWeight: 700, fontSize: 15, color: 'var(--color-text)' }}>{proposal.title || 'Untitled Proposal'}</span>
            <StatusBadge status={proposal.status} />
            {proposal.blast_radius && <BlastBadge blast_radius={proposal.blast_radius} />}
            {proposal.proposal_type && (
              <span style={{ fontSize: 12, color: 'var(--color-text-3)', fontWeight: 500 }}>
                {proposal.proposal_type.replace(/_/g, ' ')}
              </span>
            )}
          </div>

          {/* what_changes */}
          {proposal.what_changes && (
            <div style={{ fontSize: 13, color: 'var(--color-text-2)', lineHeight: 1.6, marginBottom: 8 }}>
              {proposal.what_changes}
            </div>
          )}

          {/* why_human_eyes */}
          {proposal.why_human_eyes && (
            <div style={{
              fontSize: 13,
              color: 'var(--color-yellow-text)',
              background: 'var(--color-yellow-bg)',
              border: '1px solid var(--color-yellow-border)',
              borderRadius: 8,
              padding: '10px 14px',
              marginBottom: 8,
              display: 'flex',
              alignItems: 'flex-start',
              gap: 8,
            }}>
              <Eye size={14} style={{ marginTop: 2, flexShrink: 0 }} />
              <span>{proposal.why_human_eyes}</span>
            </div>
          )}

          {/* Critic objection (amber border card) */}
          {hasObjection && (
            <div style={{
              fontSize: 13,
              color: 'var(--color-yellow-text)',
              background: 'var(--color-yellow-bg)',
              borderLeft: '3px solid var(--color-yellow-text)',
              borderRadius: '0 8px 8px 0',
              padding: '10px 14px',
              marginBottom: 8,
            }}>
              <div style={{ fontWeight: 600, marginBottom: 4, display: 'flex', alignItems: 'center', gap: 6 }}>
                <AlertTriangle size={13} />
                Critic objection
              </div>
              <div>{proposal.critic_objection}</div>
              {proposal.what_to_do && (
                <div style={{ marginTop: 6, fontStyle: 'italic', opacity: 0.85 }}>
                  Suggested action: {proposal.what_to_do}
                </div>
              )}
            </div>
          )}

          {/* Evidence summary (3 numbers) */}
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', fontSize: 12, color: 'var(--color-text-3)' }}>
            {proposal.proposal_confidence != null && (
              <span>Confidence: <strong style={{ color: 'var(--color-text-2)' }}>{(proposal.proposal_confidence * 100).toFixed(0)}%</strong></span>
            )}
            {proposal.critic_confidence != null && (
              <span>Critic: <strong style={{ color: 'var(--color-text-2)' }}>{(proposal.critic_confidence * 100).toFixed(0)}%</strong></span>
            )}
            {proposal.field_criticality && (
              <span>Criticality: <strong style={{ color: 'var(--color-text-2)' }}>{proposal.field_criticality}</strong></span>
            )}
            {proposal.reversibility && (
              <span>Reversibility: <strong style={{ color: 'var(--color-text-2)' }}>{proposal.reversibility.replace(/_/g, ' ')}</strong></span>
            )}
          </div>
        </div>
      </div>

      {/* Evidence drawer toggle */}
      {proposal.supporting_evidence && (
        <div style={{ borderTop: '1px solid var(--color-border)' }}>
          <button
            onClick={() => setExpanded(!expanded)}
            style={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              padding: '10px 24px',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              fontSize: 12,
              fontWeight: 600,
              color: 'var(--color-text-3)',
            }}
          >
            <FileText size={13} />
            Full evidence
            {expanded ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
          </button>
          {expanded && (
            <div style={{
              padding: '0 24px 16px',
              fontSize: 12,
              color: 'var(--color-text-2)',
              lineHeight: 1.6,
              whiteSpace: 'pre-wrap',
              fontFamily: 'monospace',
              background: 'var(--color-surface-2)',
              maxHeight: 300,
              overflowY: 'auto',
            }}>
              {typeof proposal.supporting_evidence === 'string'
                ? proposal.supporting_evidence
                : JSON.stringify(proposal.supporting_evidence, null, 2)}
            </div>
          )}
        </div>
      )}

      {/* Action buttons — only for critic_cleared (awaiting human review) */}
      {proposal.status === 'critic_cleared' && (
        <div style={{
          borderTop: '1px solid var(--color-border)',
          padding: '14px 24px',
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          flexWrap: 'wrap',
        }}>
          <button
            onClick={() => onApprove(proposal.id)}
            disabled={actionLoading}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              padding: '8px 18px',
              borderRadius: 8,
              border: 'none',
              background: 'var(--color-primary)',
              color: '#fff',
              fontWeight: 600,
              fontSize: 13,
              cursor: actionLoading ? 'not-allowed' : 'pointer',
              opacity: actionLoading ? 0.6 : 1,
            }}
          >
            <CheckCircle size={14} />
            Approve
          </button>

          {!showRejectInput ? (
            <button
              onClick={() => setShowRejectInput(true)}
              disabled={actionLoading}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                padding: '8px 18px',
                borderRadius: 8,
                border: '1px solid var(--color-border)',
                background: 'transparent',
                color: 'var(--color-text-2)',
                fontWeight: 600,
                fontSize: 13,
                cursor: actionLoading ? 'not-allowed' : 'pointer',
                opacity: actionLoading ? 0.6 : 1,
              }}
            >
              <XCircle size={14} />
              Reject
            </button>
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1, minWidth: 200 }}>
              <input
                type="text"
                value={rejectReason}
                onChange={e => setRejectReason(e.target.value)}
                placeholder="Reason (optional)"
                style={{
                  flex: 1,
                  padding: '8px 12px',
                  borderRadius: 8,
                  border: '1px solid var(--color-border)',
                  background: 'var(--color-surface)',
                  color: 'var(--color-text)',
                  fontSize: 13,
                  outline: 'none',
                }}
              />
              <button
                onClick={() => { onReject(proposal.id, rejectReason); setShowRejectInput(false); setRejectReason(''); }}
                disabled={actionLoading}
                style={{
                  padding: '8px 14px',
                  borderRadius: 8,
                  border: '1px solid var(--color-red-border)',
                  background: 'var(--color-red-bg)',
                  color: 'var(--color-red-text)',
                  fontWeight: 600,
                  fontSize: 13,
                  cursor: actionLoading ? 'not-allowed' : 'pointer',
                }}
              >
                Confirm
              </button>
              <button
                onClick={() => { setShowRejectInput(false); setRejectReason(''); }}
                style={{
                  padding: '8px 12px',
                  borderRadius: 8,
                  border: '1px solid var(--color-border)',
                  background: 'transparent',
                  color: 'var(--color-text-3)',
                  fontSize: 13,
                  cursor: 'pointer',
                }}
              >
                Cancel
              </button>
            </div>
          )}

          <div style={{ flex: 1 }} />
          <span style={{ fontSize: 11, color: 'var(--color-text-3)' }}>
            {proposal.created_at && new Date(proposal.created_at).toLocaleString()}
          </span>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Audit Log
// ---------------------------------------------------------------------------

function AuditLog({ proposals }) {
  const terminalStatuses = ['approved_for_canary', 'canary_live', 'live', 'reverted', 'blocked'];
  const auditItems = proposals
    .filter(p => terminalStatuses.includes(p.status))
    .sort((a, b) => {
      const dateA = a.applied_at || a.reverted_at || a.created_at || '';
      const dateB = b.applied_at || b.reverted_at || b.created_at || '';
      return dateB.localeCompare(dateA);
    });

  if (auditItems.length === 0) {
    return (
      <div style={{ fontSize: 13, color: 'var(--color-text-3)', padding: '20px 0' }}>
        No applied or reverted proposals yet.
      </div>
    );
  }

  return (
    <div style={{ border: '1px solid var(--color-border)', borderRadius: 8, overflow: 'hidden' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
        <thead>
          <tr style={{ borderBottom: '1px solid var(--color-border)' }}>
            {['Title', 'Type', 'Status', 'Date', 'Reason'].map((h, i) => (
              <th key={i} style={{ padding: '10px 14px', textAlign: 'left', fontWeight: 600, color: 'var(--color-text-2)', background: 'var(--color-surface-2)' }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {auditItems.map(p => (
            <tr key={p.id} style={{ borderBottom: '1px solid var(--color-border)' }}>
              <td style={{ padding: '10px 14px', color: 'var(--color-text)', fontWeight: 500 }}>{p.title || '—'}</td>
              <td style={{ padding: '10px 14px', color: 'var(--color-text-2)' }}>{p.proposal_type?.replace(/_/g, ' ') || '—'}</td>
              <td style={{ padding: '10px 14px' }}><StatusBadge status={p.status} /></td>
              <td style={{ padding: '10px 14px', color: 'var(--color-text-3)', fontSize: 12 }}>
                {new Date(p.applied_at || p.reverted_at || p.created_at).toLocaleString()}
              </td>
              <td style={{ padding: '10px 14px', color: 'var(--color-text-3)', fontSize: 12, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {p.revert_reason || '—'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main Page
// ---------------------------------------------------------------------------

export default function AdminProposals() {
  const navigate = useNavigate();
  const [proposals, setProposals] = useState([]);
  const [circuitBreaker, setCircuitBreaker] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [tab, setTab] = useState('pending'); // 'pending' | 'audit'

  const fetchData = useCallback(async () => {
    try {
      const [proposalsRes, cbRes] = await Promise.all([
        api.getAdminProposals(),
        api.getCircuitBreaker(),
      ]);
      setProposals(proposalsRes.proposals || []);
      setCircuitBreaker(cbRes.circuit_breaker || null);
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleApprove = async (id) => {
    setActionLoading(true);
    try {
      await api.approveProposal(id);
      await fetchData();
    } catch (err) {
      setError(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async (id, reason) => {
    setActionLoading(true);
    try {
      await api.rejectProposal(id, reason);
      await fetchData();
    } catch (err) {
      setError(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  const handleResetCircuitBreaker = async () => {
    setActionLoading(true);
    try {
      await api.resetCircuitBreaker();
      await fetchData();
    } catch (err) {
      setError(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', color: 'var(--color-text-3)' }}>
        Loading proposals...
      </div>
    );
  }

  const pendingStatuses = ['critic_cleared', 'blocked', 'proposed'];
  const pendingProposals = proposals.filter(p => pendingStatuses.includes(p.status));
  const waitingCount = pendingProposals.length;

  return (
    <div style={{ maxWidth: 900, margin: '0 auto', padding: '32px 24px' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24 }}>
        <button onClick={() => navigate('/admin')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-text-3)', padding: 4 }}>
          <ArrowLeft size={20} />
        </button>
        <h1 style={{ fontSize: 22, fontWeight: 700, color: 'var(--color-text)' }}>Improvement Proposals</h1>
      </div>

      {/* Circuit breaker banner */}
      <CircuitBreakerBanner
        circuitBreaker={circuitBreaker}
        waitingCount={waitingCount}
        onReset={handleResetCircuitBreaker}
      />

      {/* Error */}
      {error && (
        <div style={{
          background: 'var(--color-red-bg)',
          border: '1px solid var(--color-red-border)',
          borderRadius: 8,
          padding: '12px 16px',
          marginBottom: 16,
          fontSize: 13,
          color: 'var(--color-red-text)',
        }}>
          {error}
        </div>
      )}

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 24, borderBottom: '1px solid var(--color-border)', paddingBottom: 0 }}>
        {[
          { key: 'pending', label: 'Pending Review', icon: Clock, count: pendingProposals.length },
          { key: 'audit', label: 'Audit Log', icon: FileText, count: null },
        ].map(t => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              padding: '10px 18px',
              background: 'none',
              border: 'none',
              borderBottom: tab === t.key ? '2px solid var(--color-primary)' : '2px solid transparent',
              color: tab === t.key ? 'var(--color-primary)' : 'var(--color-text-3)',
              fontWeight: 600,
              fontSize: 13,
              cursor: 'pointer',
              marginBottom: -1,
            }}
          >
            <t.icon size={14} />
            {t.label}
            {t.count != null && t.count > 0 && (
              <span style={{
                background: 'var(--color-primary)',
                color: '#fff',
                borderRadius: 999,
                padding: '1px 7px',
                fontSize: 11,
                fontWeight: 700,
                marginLeft: 2,
              }}>
                {t.count}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Tab content */}
      {tab === 'pending' && (
        <>
          {pendingProposals.length === 0 ? (
            <div style={{
              textAlign: 'center',
              padding: '48px 24px',
              color: 'var(--color-text-3)',
            }}>
              <ShieldCheck size={40} style={{ marginBottom: 12, opacity: 0.4 }} />
              <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 4 }}>All clear</div>
              <div style={{ fontSize: 13 }}>No proposals awaiting review.</div>
            </div>
          ) : (
            pendingProposals.map(p => (
              <ProposalCard
                key={p.id}
                proposal={p}
                onApprove={handleApprove}
                onReject={handleReject}
                actionLoading={actionLoading}
              />
            ))
          )}
        </>
      )}

      {tab === 'audit' && (
        <AuditLog proposals={proposals} />
      )}
    </div>
  );
}
